import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { StackingZIndexContext } from "components/designV2/stacking/stackingZIndexContext";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { ZIndex } from "config/zIndex";

interface SidePanelProps {
  children: ReactNode;
  closeButtonMode?: "animated" | "icon-only";
  footer?: ReactNode;
  isOpen: boolean;
  onBackdropClick?: () => void;
  onClose: () => void;
  title: string;
  titleId?: string;
}

const BODY_SCROLL_LOCK_COUNT_KEY = "sidePanelScrollLockCount";
const BODY_PREVIOUS_OVERFLOW_KEY = "sidePanelPreviousOverflow";
const BODY_PREVIOUS_PADDING_RIGHT_KEY = "sidePanelPreviousPaddingRight";

/**
 * Locks background page scrolling while any SidePanel is open. Uses a
 * body-level ref-count so multiple overlays can coexist without unlocking
 * scroll too early.
 */
const useBodyScrollLock = (isEnabled: boolean): void => {
  useEffect(() => {
    if (!isEnabled) return;
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const body = document.body;
    const previousLockCountRaw = body.dataset[BODY_SCROLL_LOCK_COUNT_KEY];
    const previousLockCount =
      previousLockCountRaw == null ? 0 : Number(previousLockCountRaw) || 0;
    const nextLockCount = previousLockCount + 1;
    body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] = String(nextLockCount);

    if (nextLockCount === 1) {
      body.dataset[BODY_PREVIOUS_OVERFLOW_KEY] = body.style.overflow;
      body.dataset[BODY_PREVIOUS_PADDING_RIGHT_KEY] = body.style.paddingRight;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      const currentLockCountRaw = body.dataset[BODY_SCROLL_LOCK_COUNT_KEY];
      const currentLockCount =
        currentLockCountRaw == null ? 0 : Number(currentLockCountRaw) || 0;
      const updatedLockCount = Math.max(0, currentLockCount - 1);

      if (updatedLockCount === 0) {
        body.style.overflow = body.dataset[BODY_PREVIOUS_OVERFLOW_KEY] ?? "";
        body.style.paddingRight =
          body.dataset[BODY_PREVIOUS_PADDING_RIGHT_KEY] ?? "";
        delete body.dataset[BODY_SCROLL_LOCK_COUNT_KEY];
        delete body.dataset[BODY_PREVIOUS_OVERFLOW_KEY];
        delete body.dataset[BODY_PREVIOUS_PADDING_RIGHT_KEY];
        return;
      }

      body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] = String(updatedLockCount);
    };
  }, [isEnabled]);
};

/** Slide-in side panel with a backdrop, header, scrollable body, and sticky footer. */
export const SidePanel = ({
  children,
  closeButtonMode = "animated",
  footer,
  isOpen,
  onBackdropClick,
  onClose,
  title,
  titleId,
}: SidePanelProps): JSX.Element | null => {
  const { t } = useTranslation();

  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const handleBackdropClick = onBackdropClick ?? onClose;

  return (
    <StackingZIndexContext.Provider value={ZIndex.sidePanel}>
      <Overlay onClick={handleBackdropClick}>
        <Panel
          role="dialog"
          aria-modal
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
        >
          <HeaderRow>
            <Title id={titleId}>{title}</Title>
            <CloseButton
              iconName="v2/cross"
              size="small"
              expandIconLabelOnHover={closeButtonMode !== "icon-only"}
              aria-label={t("DesignComponents.CloseModal")}
              onClick={onClose}
            />
          </HeaderRow>
          <PanelBody>
            <PanelContent>{children}</PanelContent>
            {footer && <StickyFooter>{footer}</StickyFooter>}
          </PanelBody>
        </Panel>
      </Overlay>
    </StackingZIndexContext.Provider>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 18, 30, 0.45);
  display: flex;
  justify-content: flex-end;
  z-index: ${ZIndex.sidePanel};
`;

const Panel = styled.div`
  width: 520px;
  max-width: 95vw;
  height: 100%;
  max-height: 100dvh;
  background: ${ColorV2.Blue7};
  box-shadow: -12px 0 32px rgba(15, 42, 64, 0.2);
  padding: 34px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 12px;
  margin-right: -12px;
  scrollbar-width: thin;
  scrollbar-color: ${ColorV2.Blue5} transparent;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue5};
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue4};
    background-clip: content-box;
  }
`;

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin-top: auto;
  padding: 24px 0 10px;
  background: linear-gradient(
    to bottom,
    rgba(242, 246, 249, 0) 0%,
    ${ColorV2.Blue7} 30%
  );
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.h2`
  ${TypographyV2.h2};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

const CloseButton = styled(SecondaryButton)`
  flex-shrink: 0;
`;
